import * as yup from 'yup';
import { InsuranceFormType } from './PatientDetailsInsuranceForm.interface';
import dayjs from 'dayjs';
import { drivenFieldsConversion } from './PatientDetailsInsuranceForm.utils';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { t } from 'i18next';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { getEnvFnc } from '@gemini/lux-ds-pms';

const ONLY_LETTERS_REGEX = /^[A-Za-z\s]+$/;
const CITY_REGEX = /^[a-zA-Z0-9' -]+$/;
const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
const MIN_DOB = dayjs('1753-01-01');
const phoneUtil = PhoneNumberUtil.getInstance();
const LOWER_ENVIRONMENTS = new Set(['development', 'qa', 'staging']);
const TEST_PHONE_PREFIX = '555';

export const insuranceFormSchema = (
  formType: InsuranceFormType,
  insuranceDetailsOptional: boolean | undefined,
  driveMap: ReturnType<typeof drivenFieldsConversion>,
) => {
  const insuranceIdOptional =
    driveMap.insuranceId.hidden || driveMap.insuranceId.optional;
  const insuranceMemberIdOptional =
    driveMap.insuranceMemberId.hidden || driveMap.insuranceMemberId.optional;
  const isHolderOptional =
    driveMap.isHolder.hidden || driveMap.isHolder.optional;
  const firstNameOptional =
    driveMap.firstName.hidden || driveMap.firstName.optional;
  const lastNameOptional =
    driveMap.lastName.hidden || driveMap.lastName.optional;
  const genderOptional = driveMap.gender.hidden || driveMap.gender.optional;
  const dateOfBirthOptional =
    driveMap.dateOfBirth.hidden || driveMap.dateOfBirth.optional;
  const phoneOptional = driveMap.phone.hidden || driveMap.phone.optional;
  const streetAddressOptional =
    driveMap.streetAddress.hidden || driveMap.streetAddress.optional;
  const cityOptional = driveMap.city.hidden || driveMap.city.optional;
  const stateOptional = driveMap.state.hidden || driveMap.state.optional;
  const countryOptional = driveMap.country.hidden || driveMap.country.optional;
  const zipCodeOptional = driveMap.zipCode.hidden || driveMap.zipCode.optional;
  const relationshipOptional =
    driveMap.relationship.hidden || driveMap.relationship.optional;

  //function to understand if we have A MONTE the "would you like to submit..." question or not.
  // if yes -> we validate all the dependencies based on the answer (yes -> validate, no -> not validate)
  // if no -> we validate all the dependencies anyway, because the user doesn't have the chance to decide to hide them
  const shouldValidateDetailsDependencies = (
    provideInsuranceDetails?: string,
  ) =>
    provideInsuranceDetails === StaticValues.YES || !insuranceDetailsOptional;
  const requiredError = t('fieldsLabel.errors.isRequired');
  const onlyLettersError = t('fieldsLabel.errors.onlyLetters');
  const dobInvalidError = t('fieldsLabel.errors.dobInvalid');
  const dateInFutureError = t('fieldsLabel.errors.dateInFuture');
  const noSpecialCharsError = t('fieldsLabel.errors.noSpecialChars');
  const zipCodeError = t('fieldsLabel.errors.zipCode');
  const phoneInvalidError = t('fieldsLabel.errors.phoneInvalid');
  const maxLengthError = (max: number) =>
    t('fieldsLabel.errors.maxLength', { max });

  const applyRequired = (schema: yup.StringSchema, isRequired: boolean) =>
    isRequired ? schema.required(requiredError) : schema.notRequired();

  const shouldUseSameAsForPrimaryInsurance = (
    sameAsForPrimaryInsurance?: boolean,
  ) =>
    formType === InsuranceFormType.SECONDARY &&
    sameAsForPrimaryInsurance === true;

  const validateHolderField = (
    isHolder: string,
    provideInsuranceDetails: string,
    sameAsForPrimaryInsurance: boolean | undefined,
    hidden: boolean,
  ) =>
    isHolder === StaticValues.NO &&
    shouldValidateDetailsDependencies(provideInsuranceDetails) &&
    !shouldUseSameAsForPrimaryInsurance(sameAsForPrimaryInsurance) &&
    !hidden;

  const shouldValidatePhone = (
    isHolder: string,
    provideInsuranceDetails: string,
    sameAsForPrimaryInsurance?: boolean,
  ) =>
    isHolder === StaticValues.NO &&
    shouldValidateDetailsDependencies(provideInsuranceDetails) &&
    !shouldUseSameAsForPrimaryInsurance(sameAsForPrimaryInsurance) &&
    !phoneOptional;

  const isPhoneValid = (phone?: {
    number?: string | null;
    prefix?: string | null;
  }) => {
    if (!phone?.number || !phone?.prefix) return true;

    try {
      const parsedPhone = phoneUtil.parseAndKeepRawInput(
        `${phone.prefix}${phone.number}`,
      );
      const { VX_ENV = '' } = getEnvFnc();
      const isValidPhoneNumber = phoneUtil.isValidNumber(parsedPhone);

      if (isValidPhoneNumber) return true;

      const isDevelopmentOrTesting = LOWER_ENVIRONMENTS.has(VX_ENV);
      const nationalNumber = parsedPhone.getNationalNumber()?.toString() ?? '';

      return isDevelopmentOrTesting
        ? phoneUtil.isPossibleNumber(parsedPhone) &&
            nationalNumber.startsWith(TEST_PHONE_PREFIX)
        : false;
    } catch {
      return false;
    }
  };

  return yup.object().shape({
    patientDetails: yup.object().shape({
      [formType]: yup.object().shape({
        provideInsuranceDetails: yup.string().when([], {
          is: () => insuranceDetailsOptional,
          then: schema => schema.required(requiredError), // NOSONAR
          otherwise: schema => schema.notRequired(),
        }),
        insuranceId: yup
          .string()
          .default('')
          .when([], {
            is: () => !insuranceIdOptional,
            then: schema => schema.required(requiredError), // NOSONAR
            otherwise: schema => schema.notRequired(),
          }),
        insuranceNameOther: yup.string().when('insuranceId', {
          is: (insuranceId: string) =>
            insuranceId === StaticValues.OTHER_OPTION && !insuranceIdOptional,
          then: (
            schema, // NOSONAR
          ) => schema.required(requiredError).max(255, maxLengthError(255)),
          otherwise: schema => schema.notRequired(),
        }),
        insuranceMemberId: yup.string().when([], {
          is: () => !insuranceMemberIdOptional,
          then: schema => schema.required(requiredError), // NOSONAR
          otherwise: schema => schema.notRequired(),
        }),
        insuranceMemberIdOther: yup.string().when('insuranceMemberId', {
          is: (insuranceMemberId: string) =>
            insuranceMemberId === StaticValues.ADD_NEW &&
            !insuranceMemberIdOptional,
          then: schema => schema.required(requiredError), // NOSONAR
          otherwise: schema => schema.notRequired(),
        }),
        isHolder: yup.string().when('provideInsuranceDetails', {
          is: (provideInsuranceDetails: string) =>
            shouldValidateDetailsDependencies(provideInsuranceDetails) &&
            !isHolderOptional,
          then: schema => schema.required(requiredError), // NOSONAR
          otherwise: schema => schema.notRequired(),
        }),
        sameAsForPrimaryInsurance: yup.boolean().notRequired(),
        firstName: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                validateHolderField(
                  isHolder,
                  provideInsuranceDetails,
                  sameAsForPrimaryInsurance,
                  driveMap.firstName.hidden,
                ),
              // prettier-ignore
              then: schema => applyRequired(schema, !firstNameOptional).matches(ONLY_LETTERS_REGEX, { message: onlyLettersError, excludeEmptyString: true }), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        lastName: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                validateHolderField(
                  isHolder,
                  provideInsuranceDetails,
                  sameAsForPrimaryInsurance,
                  driveMap.lastName.hidden,
                ),
              // prettier-ignore
              then: schema => applyRequired(schema, !lastNameOptional).matches(ONLY_LETTERS_REGEX, { message: onlyLettersError, excludeEmptyString: true }), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        gender: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                isHolder === StaticValues.NO &&
                shouldValidateDetailsDependencies(provideInsuranceDetails) &&
                !shouldUseSameAsForPrimaryInsurance(
                  sameAsForPrimaryInsurance,
                ) &&
                !genderOptional,
              then: schema => schema.required(requiredError), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        dateOfBirth: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                isHolder === StaticValues.NO &&
                shouldValidateDetailsDependencies(provideInsuranceDetails) &&
                !shouldUseSameAsForPrimaryInsurance(
                  sameAsForPrimaryInsurance,
                ) &&
                !dateOfBirthOptional,
              //prettier-ignore
              then: ( // NOSONAR
            schema, 
          ) =>
            schema
              .required(requiredError)
              .test(
                'is-valid-date',
                dobInvalidError,
                value => {
                  if (!value) return false;
                  return dayjs(value).isValid();
                },
              )
              .test('is-min-date', dobInvalidError, value => {
                if (!value) return false;
                return !dayjs(value).isBefore(MIN_DOB, 'day');
              })
              .test('is-past-date', dateInFutureError, value => {
                if (!value) return false;
                return (
                  dayjs(value).isBefore(dayjs()) ||
                  dayjs(value).isSame(dayjs(), 'day')
                );
              }),
              otherwise: schema =>
                schema
                  .notRequired()
                  .test('is-valid-date', dobInvalidError, value => {
                    if (!value) return true;
                    return dayjs(value).isValid();
                  })
                  .test('is-min-date', dobInvalidError, value => {
                    if (!value) return true;
                    return !dayjs(value).isBefore(MIN_DOB, 'day');
                  })
                  .test('is-past-date', dateInFutureError, value => {
                    if (!value) return true;
                    return (
                      dayjs(value).isBefore(dayjs()) ||
                      dayjs(value).isSame(dayjs(), 'day')
                    );
                  }),
            },
          ),
        phone: yup
          .object()
          .shape({
            number: yup.string().notRequired(),
            prefix: yup.string().notRequired(),
          })
          .test('phone-required', requiredError, function (value) {
            const {
              isHolder,
              provideInsuranceDetails,
              sameAsForPrimaryInsurance,
            } = this.parent;
            if (
              !shouldValidatePhone(
                isHolder,
                provideInsuranceDetails,
                sameAsForPrimaryInsurance,
              )
            ) {
              return true;
            }

            return !!value?.number && !!value?.prefix;
          })
          .test('phone-valid', phoneInvalidError, value => isPhoneValid(value)),
        streetAddress: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                validateHolderField(
                  isHolder,
                  provideInsuranceDetails,
                  sameAsForPrimaryInsurance,
                  driveMap.streetAddress.hidden,
                ),
              // prettier-ignore
              then: schema => applyRequired(schema, !streetAddressOptional).max(55, maxLengthError(55)), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        city: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                validateHolderField(
                  isHolder,
                  provideInsuranceDetails,
                  sameAsForPrimaryInsurance,
                  driveMap.city.hidden,
                ),
              // prettier-ignore
              then: schema => applyRequired(schema, !cityOptional).max(50, maxLengthError(50)).matches(CITY_REGEX, { message: noSpecialCharsError, excludeEmptyString: true }), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        state: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                isHolder === StaticValues.NO &&
                shouldValidateDetailsDependencies(provideInsuranceDetails) &&
                !shouldUseSameAsForPrimaryInsurance(
                  sameAsForPrimaryInsurance,
                ) &&
                !stateOptional,
              then: schema => schema.required(requiredError), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        country: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                isHolder === StaticValues.NO &&
                shouldValidateDetailsDependencies(provideInsuranceDetails) &&
                !shouldUseSameAsForPrimaryInsurance(
                  sameAsForPrimaryInsurance,
                ) &&
                !countryOptional,
              then: schema => schema.required(requiredError), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        zipCode: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                validateHolderField(
                  isHolder,
                  provideInsuranceDetails,
                  sameAsForPrimaryInsurance,
                  driveMap.zipCode.hidden,
                ),
              // prettier-ignore
              then: schema => applyRequired(schema, !zipCodeOptional).matches(ZIP_CODE_REGEX, { message: zipCodeError, excludeEmptyString: true }), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
        relationship: yup
          .string()
          .when(
            [
              'isHolder',
              'provideInsuranceDetails',
              'sameAsForPrimaryInsurance',
            ],
            {
              is: (
                isHolder: string,
                provideInsuranceDetails: string,
                sameAsForPrimaryInsurance?: boolean,
              ) =>
                isHolder === StaticValues.NO &&
                shouldValidateDetailsDependencies(provideInsuranceDetails) &&
                !shouldUseSameAsForPrimaryInsurance(
                  sameAsForPrimaryInsurance,
                ) &&
                !relationshipOptional,
              then: schema => schema.required(requiredError), // NOSONAR
              otherwise: schema => schema.notRequired(),
            },
          ),
      }),
    }),
  });
};
